import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../AuthContext';
import { Plus, Trash2, Edit2, Package, Loader2, X, ChevronRight } from 'lucide-react';

export default function BatchManagement() {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ batch_id: '', batch_name: '', batch_notes: '', cost_of_batch: '', selling_price: '' });

  const fetchBatches = async () => {
    try {
      const res = await apiCall('/api/batches', { method: 'GET' });
      setBatches(res.data.batches || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchBatches(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, cost_of_batch: parseFloat(form.cost_of_batch) || 0, selling_price: parseFloat(form.selling_price) || 0 };
    try {
      if (editing) {
        await apiCall(`/api/batches/${editing}`, { method: 'PUT', data: payload });
      } else {
        await apiCall('/api/batches', { method: 'POST', data: payload });
      }
      setShowForm(false);
      setEditing(null);
      setForm({ batch_id: '', batch_name: '', batch_notes: '', cost_of_batch: '', selling_price: '' });
      fetchBatches();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving batch');
    }
  };

  const handleDelete = async (batchId) => {
    if (!window.confirm('Delete this batch?')) return;
    try {
      await apiCall(`/api/batches/${batchId}`, { method: 'DELETE' });
      fetchBatches();
    } catch (e) {
      alert('Error deleting batch');
    }
  };

  const openEdit = (batch) => {
    setEditing(batch.batch_id);
    setForm({ batch_id: batch.batch_id, batch_name: batch.batch_name, batch_notes: batch.batch_notes || '', cost_of_batch: String(batch.cost_of_batch), selling_price: String(batch.selling_price || 0) });
    setShowForm(true);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" />
    </div>
  );

  // Grand totals across all batches — cost paid vs total resale (selling) to see
  // at a glance whether the whole inventory is positive or negative.
  const totalCost = batches.reduce((s, b) => s + Number(b.cost_of_batch || 0), 0);
  const totalSelling = batches.reduce((s, b) => s + Number(b.products_selling_total || 0), 0);
  const totalProfit = totalSelling - totalCost;

  return (
    <div className="space-y-6" data-testid="batch-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">Batch Management</h2>
          <p className="text-sm text-slate-500 mt-1">Organize your products into batches and track costs</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ batch_id: '', batch_name: '', batch_notes: '', cost_of_batch: '', selling_price: '' }); }}
          className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg px-4 py-2.5 text-sm transition-colors"
          data-testid="create-batch-button">
          <Plus className="w-4 h-4" /> New Batch
        </button>
      </div>

      {/* Batch Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="batch-form-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#0F172A]">{editing ? 'Edit Batch' : 'Create Batch'}</h3>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Batch ID</label>
                <input value={form.batch_id} onChange={e => setForm(p => ({ ...p, batch_id: e.target.value }))} required disabled={!!editing}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm disabled:bg-slate-50"
                  placeholder="BATCH-001" data-testid="batch-id-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Batch Name</label>
                <input value={form.batch_name} onChange={e => setForm(p => ({ ...p, batch_name: e.target.value }))} required
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  placeholder="Spring Collection" data-testid="batch-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Notes</label>
                <textarea value={form.batch_notes} onChange={e => setForm(p => ({ ...p, batch_notes: e.target.value }))} rows={2}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm resize-none"
                  placeholder="Optional notes..." data-testid="batch-notes-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Cost ($)</label>
                  <input type="number" step="0.01" value={form.cost_of_batch} onChange={e => setForm(p => ({ ...p, cost_of_batch: e.target.value }))} required
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                    placeholder="0.00" data-testid="batch-cost-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Selling Price ($)</label>
                  <input type="number" step="0.01" value={form.selling_price} onChange={e => setForm(p => ({ ...p, selling_price: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                    placeholder="0.00" data-testid="batch-selling-price-input" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="flex-1 py-2.5 rounded-lg font-medium text-sm border border-slate-200 hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button type="submit" data-testid="batch-submit-button"
                  className="flex-1 bg-[#0052FF] hover:bg-[#0040CC] text-white py-2.5 rounded-lg font-medium text-sm transition-colors">
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Batch List */}
      {batches.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="empty-batches">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">No batches yet</p>
          <p className="text-sm text-slate-400 mt-1">Create your first batch to start organizing products</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="batch-list">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3">Batch ID</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3">Name</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3">Cost</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3">Selling Total</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3">Profit</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3">Items</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3">Created</th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b, i) => {
                  const sellingTotal = Number(b.products_selling_total || 0);
                  const profit = Number(b.projected_profit || 0);
                  const profitColor = profit > 0 ? 'text-green-600' : profit < 0 ? 'text-red-600' : 'text-slate-500';
                  return (
                  <tr key={b.batch_id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${i === batches.length - 1 ? 'border-b-0' : ''}`}
                    onClick={() => navigate(`/batches/${encodeURIComponent(b.batch_id)}`)}
                    data-testid={`batch-row-${b.batch_id}`}>
                    <td className="px-6 py-4 text-sm font-medium text-[#0F172A]">
                      <span className="inline-flex items-center gap-1.5 group-hover:text-[#0052FF]">
                        {b.batch_id}
                        <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{b.batch_name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">${Number(b.cost_of_batch || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-[#0F172A]" data-testid={`batch-selling-total-${b.batch_id}`}>${sellingTotal.toFixed(2)}</td>
                    <td className={`px-6 py-4 text-sm font-semibold ${profitColor}`} data-testid={`batch-profit-${b.batch_id}`}>{profit >= 0 ? '+' : ''}${profit.toFixed(2)}</td>
                    <td className="px-6 py-4 text-xs text-slate-500">{b.product_count || 0} SKUs · {b.total_units || 0} units</td>
                    <td className="px-6 py-4 text-sm text-slate-500">{b.created_at ? new Date(b.created_at).toLocaleDateString() : '-'}</td>
                    <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()}>
                      <button onClick={() => openEdit(b)} className="text-slate-400 hover:text-[#0052FF] mr-3" data-testid={`edit-batch-${b.batch_id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(b.batch_id)} className="text-slate-400 hover:text-red-500" data-testid={`delete-batch-${b.batch_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold" data-testid="batch-totals-row">
                  <td className="px-6 py-4 text-sm text-[#0F172A]" colSpan={2}>Totals ({batches.length} batches)</td>
                  <td className="px-6 py-4 text-sm text-[#0F172A]" data-testid="batch-total-cost">${totalCost.toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-[#0F172A]" data-testid="batch-total-selling">${totalSelling.toFixed(2)}</td>
                  <td className={`px-6 py-4 text-sm ${totalProfit > 0 ? 'text-green-600' : totalProfit < 0 ? 'text-red-600' : 'text-slate-500'}`} data-testid="batch-total-profit">
                    {totalProfit >= 0 ? '+' : ''}${totalProfit.toFixed(2)} {totalProfit >= 0 ? '(positive)' : '(negative)'}
                  </td>
                  <td className="px-6 py-4" colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
